import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: '1. Introduction',
      content: `AppsLab LLC ("AppsLab," "we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our applications, utilities, and games (each, an "App," and collectively, the "Apps"), or interact with our website.

We believe in complete transparency about our data practices. This policy describes what information we collect, how it is used and shared, how we secure it, and the choices available to you.

By using any of our Apps or this website, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not use our Apps or this website.`
    },
    {
      title: '2. Information We Collect',
      content: `The specific information collected can vary by App, but generally includes:

Automatically Collected Data:
• Non-personal technical information such as device type, operating system, and app version
• Usage data such as feature interactions, session length, and crash or diagnostic reports
• Online identifiers such as IP address or advertising identifiers, where applicable

Voluntarily Provided Information:
• Information you choose to provide, such as your name and email address, when you contact support
• Any feedback, bug reports, or correspondence you send us

App Permissions:
• Depending on the App, we may request permissions (such as notifications or storage access) required for specific features
• Permissions are requested explicitly, and you can decline or revoke them at any time through your device settings

We do not sell your Personal Information to third parties.`
    },
    {
      title: '3. How We Use Information',
      content: `We use the information we collect to:

• Provide, operate, and maintain our Apps and this website
• Respond to support requests and other communications
• Analyze anonymous usage patterns to fix bugs and improve features
• Ensure the security and integrity of our Apps
• Comply with applicable laws and legal obligations

We will never sell your information to data brokers or use it for purposes incompatible with the ones described in this policy without your consent.`
    },
    {
      title: '4. Data Storage and Security',
      content: `We take reasonable technical and organizational measures to protect the information we collect, including:

• Encrypting sensitive data in transit and at rest where applicable
• Limiting access to personal information to personnel who need it
• Regularly reviewing our security practices as our Apps evolve

No method of transmission or storage is 100% secure, and we cannot guarantee absolute security. We encourage you to use strong, unique passwords for any accounts and to keep your devices up to date.`
    },
    {
      title: '5. Third-Party Services',
      content: `Our Apps and website may rely on third-party services for functionality such as analytics, crash reporting, cloud infrastructure, or advertising. These providers process data on our behalf and are contractually required to safeguard it in line with applicable law.

We do not control the privacy practices of third-party services linked from our Apps or website, and we encourage you to review their respective privacy policies.`
    },
    {
      title: '6. Your Rights and Choices',
      content: `Depending on your location, you may have rights regarding your personal information, including:

• The right to access the personal information we hold about you
• The right to request correction of inaccurate information
• The right to request deletion of your information
• The right to restrict or object to certain processing
• The right to data portability

If you are in the European Economic Area (EEA), UK, or a jurisdiction with similar protections, these rights are available to you under applicable law.

To exercise any of these rights, contact us at contact@appslabllc.com.`
    },
    {
      title: '7. Children\'s Privacy',
      content: `Our Apps and website are not directed at children under 13 (or the relevant minimum age in your jurisdiction), and we do not knowingly collect personal information from children.

If you are a parent or guardian and believe your child has provided us with personal information, please contact us at contact@appslabllc.com so we can take appropriate action.`
    },
    {
      title: '8. International Data Transfers',
      content: `We may process and store information in countries other than your own. Where we transfer personal information internationally, we use appropriate safeguards, such as standard contractual clauses, to help ensure your information continues to receive an adequate level of protection.`
    },
    {
      title: '9. Changes to This Privacy Policy',
      content: `We may update this Privacy Policy from time to time to reflect changes in our practices or for legal or regulatory reasons.

When we make changes, we will update the "Last Updated" date below. Continued use of our Apps or website after changes take effect constitutes acceptance of the revised policy.`
    },
    {
      title: '10. Contact Us',
      content: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

Email: contact@appslabllc.com
Website: https://appslabllc.com

We aim to respond to legitimate requests within 30 days.

Last Updated: July 2026`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 via-teal-700 to-ember-700 py-12 border-b border-teal-800/30">
        <div className="container mx-auto section-padding">
          <Link
            to="/"
            className="inline-flex items-center gap-2 mb-4 text-teal-100 hover:text-white transition-colors text-sm"
          >
            ← Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-4 text-white">
              Privacy Policy
            </h1>
            <p className="text-lg text-teal-100">
              Your privacy matters to us. Learn how we protect your data.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto section-padding py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-cloud-50 rounded-3xl border border-cloud-200 p-8 md:p-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={index < sections.length - 1 ? 'mb-12' : ''}
              >
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-teal-600 font-display">
                  {section.title}
                </h2>
                <div className="text-cloud-700 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA at bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="mb-6 text-cloud-600 text-lg">
              Questions about how we handle your data?
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-500 text-white font-bold rounded-xl hover:from-teal-500 hover:to-teal-400 transition-all shadow-lg shadow-teal-500/30"
            >
              Back to Home
            </Link>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-cloud-200 py-8">
        <div className="container mx-auto section-padding">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-700 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-cloud-900 font-display">AppsLab</span>
            </div>
            <div className="flex gap-6 text-sm text-cloud-500">
              <Link to="/terms" className="hover:text-teal-600 transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy" className="hover:text-teal-600 transition-colors">
                Privacy Policy
              </Link>
              <a href="mailto:contact@appslabllc.com" className="hover:text-teal-600 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
